import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mutationWarningsToMessages } from '../../misc/utils'
import { useHistory } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import useGlobalLoader from '../../hooks/useGlobalLoader'
import { useSnackbar } from 'notistack'
import { CategoryListQueryResult, useCategoryListQuery } from '@graphql/queries/__generated__/category.generated'
import { CartDocument, useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import { useCartUpdateProductVariantMutation } from '@graphql/mutations/__generated__/product-reflection.generated'
import {
  useClearDefaultKitSuppliesMutation,
  useCartClearMutation,
  useCartLinesUpdateBySupplyMutation,
} from '@graphql/mutations/__generated__/cart.generated'
import { HOME_URL } from '../../urls'
import { WholesaleCartParams } from '@components/WholesaleCartLines/types'
import ProductDetailsDialog from '@components/Product/ProductDetailsDialog'
import CartLineEditPanel from '../../Pages/Cart/components/CartLineEditPanel'
import Loader from '@components/Loader'
import CartLinesCategory from './CartLinesCategory'
import { Link } from 'react-router-dom'
import Button from '@mui/material/Button'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

interface Props {
  readonly?: boolean
  collapsed?: boolean
}

const WholesaleCartLinesContainer: React.FC<Props> = ({ readonly, collapsed }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [, setParams] = useQueryParams(WholesaleCartParams)
  const { setLoading } = useGlobalLoader()
  const { enqueueSnackbar } = useSnackbar()
  const [lineLoading, setLineLoading] = useState<number | null>(null)
  const [productDialogData, setProductDialogData] = useState<{
    pk: string | number
    slug: string
  } | null>(null)

  const categoryListQueryResult = useCategoryListQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: CartTypeEnum.Wholesale, includeLinesByCategory: true },
  })

  const [cartUpdateVariant] = useCartUpdateProductVariantMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Wholesale, includeLinesByCategory: true },
      },
    ],
  })
  const [cartLinesUpdateBySupply] = useCartLinesUpdateBySupplyMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Wholesale, includeLinesByCategory: true },
      },
    ],
  })
  const [cartClear] = useCartClearMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Wholesale, includeLinesByCategory: true },
      },
    ],
  })
  const [clearDefaultKitSupplies] = useClearDefaultKitSuppliesMutation()

  const onClickProduct = (id: number, slug: string) => {
    setProductDialogData({ pk: id, slug })
  }

  const handleEditQty = async (linePk: number, qty: number, variantPk: number) => {
    setLineLoading(linePk)
    try {
      const response = await cartUpdateVariant({
        variables: {
          variant: variantPk,
          cartLine: linePk,
          quantity: qty,
          cartType: CartTypeEnum.Wholesale,
        },
      })
      if (response.data.cartUpdateVariant.errors?.length) {
        throw Error(response.data.cartUpdateVariant.errors[0].messages[0])
      }
      enqueueSnackbar(
        t('snackbarMessages.success.shoppingBagModified', 'Your shopping bag has been modified successfully!'),
        {
          variant: 'success',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const handleRemoveLine = async (linePk: number, variantPk: number) => {
    await handleEditQty(linePk, 0, variantPk)
  }

  const handleEditLine = async (linePk: number) => {
    setParams({ editLinePk: linePk }, 'replaceIn')
  }
  const handleApplySupplyToAll = async (supplyLineId: string) => {
    setLoading(true)
    try {
      const response = await cartLinesUpdateBySupply({
        variables: { cartType: CartTypeEnum.Wholesale, supplyLineId },
      })
      mutationWarningsToMessages(response.data?.cartLinesUpdateBySupply.warnings).forEach(message =>
        enqueueSnackbar(message, { variant: 'warning' })
      )
      mutationWarningsToMessages(response.data?.cartLinesUpdateBySupply.errors).forEach(message =>
        enqueueSnackbar(message, { variant: 'error' })
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }

  const handleClearCart = async (categoryId?: string) => {
    setLoading(true)
    try {
      await cartClear({ variables: { cartType: CartTypeEnum.Wholesale, category: categoryId } })

      // redirect after clear all lines from cart
      if (!categoryId) {
        history.push(HOME_URL)
      }
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }

  const categories = categoryListQueryResult.data?.categories || []
  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}
  const sortedCategories: CategoryListQueryResult['data']['categories'] = categories.filter(cat =>
    Object.keys(categoriesWithLines).includes(cat.pk.toString())
  )

  return (
    <div>
      {!sortedCategories.length && (
        <div className='bg-white shadow-card rounded-lg relative'>
          <div className='py-96 px-20 text-center relative flex flex-col items-center'>
            <Loader
              className='spinner spinner--small'
              type='ThreeDots'
              color='#333'
              visible={cartQueryResult.loading}
              height={100}
              width={100}
            />
            {!cartQueryResult.loading && (
              <>
                <ShoppingBagOutlinedIcon className='text-gray-300 mb-20' sx={{ fontSize: 80 }} />
                <span className='text-xl font-bold'>{t('yourShoppingBagIsEmpty', 'Your Shopping Bag is empty.')}</span>
                <span className='my-10 text-13 text-gray-700'>
                  {t('description.shoppingBagIsEmpty', 'Looks like you haven’t added anything yet.')}
                </span>
                <Button
                  component={Link}
                  to={HOME_URL}
                  variant='contained'
                  color='secondary'
                  disableElevation
                  disableRipple
                  size='large'
                  className='mt-30 px-40'
                >
                  <span className='font-bold'>{t('button.continueShopping', 'Continue Shopping')}</span>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {sortedCategories.map((cat, idx) => (
        <CartLinesCategory
          key={cat.pk}
          category={cat}
          lineLoading={lineLoading}
          onClickProduct={onClickProduct}
          handleApplySupplyToAll={handleApplySupplyToAll}
          handleEditLine={handleEditLine}
          handleRemoveLine={handleRemoveLine}
          handleEditQty={handleEditQty}
          initialize={!collapsed && idx === 0}
          cartClear={handleClearCart}
          categoryLinesInfo={categoriesWithLines[cat.pk] || []}
          readonly={readonly}
        />
      ))}

      <ProductDetailsDialog spec={productDialogData} isOpen={!!productDialogData} setIsOpen={setProductDialogData} />
      <CartLineEditPanel setLoading={setLineLoading} cartType={CartTypeEnum.Wholesale} />
    </div>
  )
}

export default WholesaleCartLinesContainer
